import React, { useCallback, useEffect, useState } from 'react'

import { Tooltip as ReactTooltip } from 'react-tooltip'

import { Box, TextSpan } from 'app/components/design-system-components'
import { Modal } from 'app/components/design-system-components'
import { Text } from 'app/components/design-system-components/typography'
import theme from 'app/match/applicationReportPDF/assets/theme'
import {
  confirmOfferMinimumStandards,
  getOfferMinimumStandardsConfirmation,
} from 'app/services/http/offers'
import * as FormComponent from 'app/shared_components/form_component'
import { history } from 'app/shared_components/router'
import {
  propertyForceRelistKey,
  publishingOptions,
  urlIds,
  urlTo,
} from 'app/sm/helpers'
import Breadcrumbs from 'app/sm/ppp/breadcrumbs'
import PublishingOptionsAlert from 'app/sm/publishing_options_alert/publishing_options_alert'
import { formatAddress, isStateVIC } from 'app/utils/address/helpers'
import { MINIMUM_STANDARDS_METHODS } from 'app/utils/onlist/helpers'

export const OfferPublishingOptions = ({
  offer = {},
  update,
  fetchOffer,
  responseText,
  spinner,
  match,
}) => {
  const { url, params } = match || {}
  const { id: propertyId, offerId } = params || {}
  const isCreating = url.includes('/create')
  const { publishingOption: offerPublishingOption, property } = offer
  const { address = {} } = property || {}
  const { state = '' } = address || {}
  const isVIC = isStateVIC(state)

  const [publishingOption, setPublishingOption] = useState(
    offerPublishingOption,
  )

  const [
    showMinimumStandardsConfirmModal,
    setShowMinimumStandardsConfirmModal,
  ] = useState(false)

  const [confirmationData, setConfirmationData] = useState(null)

  const toggleMinimumStandardsConfirmModal = () => {
    setShowMinimumStandardsConfirmModal(!showMinimumStandardsConfirmModal)
  }

  const handleConfirmLive = async () => {
    try {
      if (offerId) {
        await confirmOfferMinimumStandards(offerId)
      }

      setPublishingOption(publishingOptions.PublishedOption)
      continueHandler()
      toggleMinimumStandardsConfirmModal()
    } catch (error) {
      console.error('Error confirming minimum standards:', error)
    }
  }

  const handleCloseMinimumStandardsConfirmModal = () => {
    toggleMinimumStandardsConfirmModal()
    setPublishingOption(offerPublishingOption)
  }

  const renderMinimumStandardsModal = () => {
    const addressStr = formatAddress(address)

    return (
      showMinimumStandardsConfirmModal &&
      isVIC && (
        <Modal
          modalBody={
            <Box width="100%">
              <TextSpan lineHeight={theme.lineHeights.encrption}>
                Please confirm {addressStr} meets the Consumer Affairs Victoria
                Minimum Standards to publish this property. This confirmation
                will be recorded.
              </TextSpan>
            </Box>
          }
          takeDecreasedWidth
          toggleModal={handleCloseMinimumStandardsConfirmModal}
          modalHeading="Minimum Standards confirmation"
          secondaryAction={handleCloseMinimumStandardsConfirmModal}
          secondaryLabel="Cancel"
          primaryLabel="Confirm"
          primaryAction={handleConfirmLive}
        />
      )
    )
  }

  const minimumStandardModal = renderMinimumStandardsModal()

  const [isPropertyForceRelist, setIsPropertyForceRelist] = useState(false)
  const [disablePropertyForceRelist, setDisblePropertyForceRelist] =
    useState(true)

  const fetchOfferDetail = useCallback(() => {
    fetchOffer(offerId)
  }, [fetchOffer, offerId])

  const fetchConfirmationData = useCallback(async () => {
    if (!isVIC || !offerId) return

    try {
      const response = await getOfferMinimumStandardsConfirmation(offerId)
      setConfirmationData(response)
    } catch (error) {
      console.error('Error fetching confirmation data:', error)
      setConfirmationData(null)
    }
  }, [isVIC, offerId])

  useEffect(() => {
    fetchOfferDetail(offerId)
    fetchConfirmationData()
  }, [propertyId, offerId, fetchOfferDetail, fetchConfirmationData])

  useEffect(() => {
    if (!!offer.propertyOfferRegister) {
      setIsPropertyForceRelist(
        offer.propertyOfferRegister?.forceRelistOnNextUpdate,
      )
      setDisblePropertyForceRelist(false)
    }
  }, [offer])

  useEffect(() => {
    setPublishingOption(offerPublishingOption)
  }, [offerPublishingOption])

  const continueHandler = () => {
    update({
      guid: offerId,
      publishingOption,
      ...(!disablePropertyForceRelist && {
        forceRelistOnNextUpdate: isPropertyForceRelist,
      }),
    })
  }

  const backHandler = () => {
    history.push(
      urlTo(urlIds.updateOfferBase, {
        propertyId,
        offerId,
      }),
    )
  }

  const handleLiveCheckboxChange = () => {
    if (isVIC && offerPublishingOption !== publishingOptions.PublishedOption) {
      toggleMinimumStandardsConfirmModal()
    }
    setPublishingOption(publishingOptions.PublishedOption)
  }

  return (
    <div className="profile-screen">
      <Breadcrumbs activeBreadCrumb="publishing-options" />
      <div className="mobile-only">
        <button
          onClick={backHandler}
          className="btn btn-transparent btn-left xs-text-center wa"
        >
          <i className="icon-arrow-left left"></i>
          <span>Back</span>
        </button>
      </div>

      <div className="two-col-box sm64">
        <div className="col-first xs-second">
          <div className="section-title">
            <div className="left column">
              <h3>Publishing Options</h3>
              {isVIC && (
                <PublishingOptionsAlert
                  isCreating={isCreating}
                  hasConfirmation={!!confirmationData?.confirmation}
                  confirmedBy={confirmationData?.confirmation?.confirmedBy}
                  confirmedAt={confirmationData?.confirmation?.confirmedAt}
                  isImplicitFeedConfirmation={
                    confirmationData?.confirmation?.method ===
                    MINIMUM_STANDARDS_METHODS.FEED
                  }
                  listedAt={confirmationData?.uploadedAt}
                  listedBy={confirmationData?.uploadedBy}
                  isPublishedOption={
                    publishingOption === publishingOptions.PublishedOption
                  }
                />
              )}
            </div>
          </div>

          <div className="display-flex mobile-flex-direction-column col-sm-12 p0">
            <FormComponent.RadioGroup
              label=""
              id="published"
              field="published"
              componentClassName="property-details-radio-group col-sm-12 pr0"
            >
              <Box>
                <FormComponent.Checkbox
                  id={publishingOptions.PublishedOption}
                  label="Live (will display and prospect enquirers)"
                  checked={
                    publishingOption === publishingOptions.PublishedOption
                  }
                  onChange={handleLiveCheckboxChange}
                />
                <FormComponent.Checkbox
                  id={publishingOptions.OffMarketOption}
                  label="Live Off Market (not displayed, no prospecting)"
                  checked={
                    publishingOption === publishingOptions.OffMarketOption
                  }
                  onChange={() =>
                    setPublishingOption(publishingOptions.OffMarketOption)
                  }
                />
                <FormComponent.Checkbox
                  id={publishingOptions.LeasedOption}
                  label="Leased (not displayed, no prospecting, no applications)"
                  checked={publishingOption === publishingOptions.LeasedOption}
                  onChange={() =>
                    setPublishingOption(publishingOptions.LeasedOption)
                  }
                />

                <Box mt={`${theme.dividedSpace[6]}px`}>
                  <hr />
                </Box>

                <Box>
                  <FormComponent.Checkbox
                    id={propertyForceRelistKey}
                    label="Create new property on next feed update"
                    checked={isPropertyForceRelist}
                    onChange={() => setIsPropertyForceRelist((prev) => !prev)}
                    showDisabledGrey={true}
                    enableEdit={!disablePropertyForceRelist}
                    readOnly={disablePropertyForceRelist}
                    showCheckboxTooltip={true}
                    toolTip={
                      <div>
                        <i
                          className="icon-help-outline fs16 gray-light-color ibm"
                          data-tooltip-content=""
                          data-tooltip-id="enabledPropertyForceRelist"
                        />
                      </div>
                    }
                  />
                  <Box>
                    <ReactTooltip
                      className="tool-tip-style"
                      id="enabledPropertyForceRelist"
                    >
                      <Box maxWidth="240px" textAlign="center">
                        <Text
                          as="span"
                          fontSize={theme.fontSizes['pRegular14']}
                        >
                          The next property feed update will result in a new
                          property being created. The existing should be
                          archived.
                        </Text>
                      </Box>
                    </ReactTooltip>
                    {disablePropertyForceRelist && (
                      <ReactTooltip
                        className="tool-tip-style"
                        id={propertyForceRelistKey}
                      >
                        <Box maxWidth="240px" textAlign="center">
                          <Text
                            as="span"
                            fontSize={theme.fontSizes['pRegular14']}
                          >
                            This property is not connected to a property feed.
                            It might be manually added or previously marked to
                            re-list as a new property.
                          </Text>
                        </Box>
                      </ReactTooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </FormComponent.RadioGroup>
          </div>
          {minimumStandardModal}
          {responseText && (
            <div className="alert alert-danger">{responseText}</div>
          )}

          <div className="row mt25 mobile-form-button">
            <div className="col-sm-5 pb5 tablet-only">
              <button
                onClick={backHandler}
                className="btn btn-transparent btn-left xs-text-center wa"
              >
                <i className="icon-arrow-left left"></i>
                <span>Back</span>
              </button>
            </div>
            <div className="col-sm-7 pb5">
              <button onClick={() => continueHandler()}>
                Continue{' '}
                <i className={spinner ? 'fa fa-spinner fa-pulse' : ''} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
